// First-touch campaign attribution: captured on the first page a visitor lands on and kept for 30 days.
// Persistent storage is only used with analytics or marketing consent; otherwise the record lives in the tab session.
export const attributionStorageKey = 'palmate-first-touch';
export const attributionTtlMs = 30 * 24 * 60 * 60 * 1000;
export const clickIdKeys = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'msclkid', 'ttclid', 'yclid', 'li_fat_id'];

export function parseCampaign(href) {
  let params;
  try { params = new URL(href).searchParams; } catch { return null; }
  const utm = {};
  const clickIds = {};
  params.forEach((value, key) => {
    const name = key.toLowerCase();
    const trimmed = String(value).trim();
    if (!trimmed) return;
    if (name.startsWith('utm_')) utm[name] = trimmed;
    else if (clickIdKeys.includes(name)) clickIds[name] = trimmed;
  });
  const campaign = {};
  if (Object.keys(utm).length) campaign.utm = utm;
  if (Object.keys(clickIds).length) campaign.clickIds = clickIds;
  return Object.keys(campaign).length ? campaign : null;
}

function readRecord(storage, now) {
  try {
    const raw = storage?.getItem?.(attributionStorageKey);
    if (!raw) return null;
    const record = JSON.parse(raw);
    if (record?.version !== 1 || typeof record.captured_at !== 'number') return null;
    if (now - record.captured_at > attributionTtlMs) return null;
    return record;
  } catch { return null; }
}
function writeRecord(storage, record) { try { storage?.setItem?.(attributionStorageKey, JSON.stringify(record)); } catch {} }
function removeRecord(storage) { try { storage?.removeItem?.(attributionStorageKey); } catch {} }

export function readFirstTouch({ storage, sessionStorage, now = Date.now() } = {}) {
  return readRecord(storage, now) || readRecord(sessionStorage, now);
}

export function captureFirstTouch({ href, referrer, storage, sessionStorage, persistent = false, now = Date.now() } = {}) {
  const existing = readFirstTouch({ storage, sessionStorage, now });
  const campaign = href ? parseCampaign(href) : null;
  if (existing && (existing.utm || existing.clickIds || !campaign)) {
    // Consent granted after the first page: move the session record to persistent storage.
    if (persistent && !readRecord(storage, now)) { writeRecord(storage, existing); removeRecord(sessionStorage); }
    return existing;
  }
  if (!href) return null;
  // A first visit without campaign data is upgraded by the first campaign visit; the original arrival time is kept.
  const record = { version: 1, captured_at: now, landing_page: href, referrer: referrer || null, ...(campaign || {}) };
  if (existing) record.first_seen_at = existing.first_seen_at || existing.captured_at;
  writeRecord(persistent ? storage : sessionStorage, record);
  removeRecord(persistent ? sessionStorage : storage);
  return record;
}
